/**
 * Shared reduce-motion hook for web components.
 *
 * Tracks the `prefers-reduced-motion` media query, reacting to runtime
 * changes. Lazy-inits from the query so a reduce-motion user never sees a
 * first-paint transition before the effect runs. Consolidates the previously
 * duplicated pattern from StatusItem.web and ExpandableCallout.web.
 */

import { useEffect, useState } from 'react';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/** One-shot check of the current `prefers-reduced-motion` preference. */
export function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia(REDUCED_MOTION_QUERY).matches
  );
}

/**
 * Returns true when the user has requested reduced motion at the OS level.
 * Listens for changes so a runtime toggle is picked up without remount.
 */
export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(prefersReducedMotion);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const query = window.matchMedia(REDUCED_MOTION_QUERY);
    setReduced(query.matches);
    const onChange = (event: MediaQueryListEvent) => setReduced(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  return reduced;
}
